import api from './client';
import { Timestamp } from 'firebase/firestore';
import type { Project } from 'types';
import type { ListParams } from './types';

// --- API Response types (ISO date strings from backend) ---

interface ProjectApiResponse {
  id: string;
  infos: any;
  org_id: string;
  author_id: string;
  status: string;
  history_events: any[];
  themes: any[];
  photos: any[];
  folders: any[];
  current_step?: string;
  last_completed_cp?: string;
}

export interface ProjectLocation {
  id: string;
  org_id?: string;
  name: string;
  city: string;
  num: string;
  status?: string;
  coordinates?: { latitude: number; longitude: number } | null;
  conductor_id?: string | null;
  commercial_id?: string | null;
}

// --- Lightweight in-memory cache for the projects map ---
// Plain module variable (not a reactive store) so it costs nothing to keep
// around and triggers no re-renders. Holds only the small ProjectLocation[]
// list, so its memory footprint is negligible. The map modal seeds from it
// for an instant open, then always refetches in the background to stay fresh.
// Any write that can change a project's position (see `update` below)
// invalidates it, so a moved/created project is never served stale.
let _locationsCache: ProjectLocation[] | null = null;

export const locationsCache = {
  get: (): ProjectLocation[] | null => _locationsCache,
  set: (data: ProjectLocation[]) => {
    _locationsCache = data;
  },
  invalidate: () => {
    _locationsCache = null;
  },
};

// --- Date conversion helpers ---

/**
 * Convert an ISO date string or datetime from API to a Firebase Timestamp.
 * Returns Timestamp.now() if the value is falsy.
 */
function toTimestamp(value: any): Timestamp {
  if (!value) return Timestamp.now();
  if (value instanceof Timestamp) return value;
  if (typeof value === 'string') {
    return Timestamp.fromDate(new Date(value));
  }
  // Handle Firestore-like {seconds, nanoseconds} objects
  if (value.seconds !== undefined) {
    return new Timestamp(value.seconds, value.nanoseconds || 0);
  }
  return Timestamp.now();
}

/**
 * Convert a Firebase Timestamp (or any date-like value) to an ISO string for sending to the API.
 * Handles proper Timestamps, plain {seconds, nanoseconds} objects, and Date objects defensively.
 */
function fromTimestamp(ts: any): string {
  if (!ts) return new Date().toISOString();
  if (ts instanceof Timestamp) return ts.toDate().toISOString();
  if (ts instanceof Date) return ts.toISOString();
  if (typeof ts === 'string') return ts;
  if (typeof ts === 'object' && ts.seconds !== undefined) {
    return new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6).toISOString();
  }
  return new Date().toISOString();
}

/**
 * Convert an API project response to a frontend Project type,
 * converting all ISO date strings to Firebase Timestamps.
 */
function apiResponseToProject(response: ProjectApiResponse): Project {
  return {
    ...response,
    infos: {
      ...response.infos,
      general: {
        ...response.infos.general,
        creation_date: toTimestamp(response.infos.general?.creation_date),
        last_updated: toTimestamp(response.infos.general?.last_updated),
      },
      client: {
        ...response.infos.client,
        contacts: response.infos.client?.contacts || [],
        last_activity_date: response.infos.client?.last_activity_date
          ? toTimestamp(response.infos.client.last_activity_date)
          : undefined,
      },
      team: response.infos.team || {},
      partners: response.infos.partners || [],
    },
    history_events: (response.history_events || []).map((event: any) => ({
      ...event,
      date: toTimestamp(event.date),
      control_point: event.control_point || undefined,
      email: event.email
        ? {
            ...event.email,
          }
        : undefined,
    })),
    themes: response.themes || [],
    photos: response.photos || [],
    folders: response.folders || [],
  };
}

/**
 * Convert a frontend Project to API-compatible format,
 * converting Firebase Timestamps to ISO strings.
 */
function projectToApiPayload(project: Project): any {
  return {
    ...project,
    infos: {
      ...project.infos,
      general: {
        ...project.infos.general,
        creation_date: fromTimestamp(project.infos.general.creation_date),
        last_updated: fromTimestamp(project.infos.general.last_updated),
      },
      client: {
        ...project.infos.client,
        last_activity_date: project.infos.client.last_activity_date
          ? fromTimestamp(project.infos.client.last_activity_date)
          : undefined,
      },
    },
    themes: (project.themes || []).map((theme) => ({
      ...theme,
      steps: (theme.steps || []).map((step) => ({
        ...step,
        controlpoints: (step.controlpoints || []).map((cp) => ({
          ...cp,
          email: cp.email
            ? {
                ...cp.email,
                date: cp.email.date ? fromTimestamp(cp.email.date) : undefined,
              }
            : undefined,
        })),
      })),
    })),
    history_events: (project.history_events || []).map((event) => ({
      ...event,
      date: fromTimestamp(event.date),
    })),
  };
}

// --- API service ---

export const projectsApi = {
  /**
   * List all projects, optionally filtered by org and status
   */
  list: async (params?: ListParams & {
    org_id?: string;
    status?: string;
    offset?: number;
    sort_by?: string;
    sort_dir?: string;
    billing_template_id?: string;
    conductor_id?: string;
    commercial_id?: string;
    /**
     * If true, the API only returns projects with at least one billing
     * retard (late payment, project delay, or step gap). Backed by the
     * pre-computed `billing.billing_flags.has_any_retard` flag.
     */
    with_billing_retards?: boolean;
  }): Promise<Project[]> => {
    const { data } = await api.get('/projects', { params });
    return (data as ProjectApiResponse[]).map(apiResponseToProject);
  },

  /**
   * Lightweight project list for the map — returns every project the
   * current user can see, with just the fields needed for map rendering
   * and filtering. Access scoping is applied server-side.
   */
  listLocations: async (params?: {
    org_id?: string;
    conductor_id?: string;
  }): Promise<ProjectLocation[]> => {
    const { data } = await api.get('/projects/locations', { params });
    return data as ProjectLocation[];
  },

  /**
   * Get a specific project by ID
   */
  get: async (projectId: string): Promise<Project> => {
    const { data } = await api.get(`/projects/${projectId}`);
    return apiResponseToProject(data);
  },

  /**
   * Create a new project
   */
  create: async (project: {
    name: string;
    num?: string;
    org_id: string;
    status?: string;
    address?: string;
    city?: string;
    postal?: string;
    country?: string;
    notes?: string;
    home_address?: string;
    home_city?: string;
    home_postal?: string;
    lot?: string;
    section?: string;
    cadastre_number?: string;
    themes?: any[];
    folders?: any[];
    contacts?: any[];
    partners?: any[];
    team?: any;
    billing?: any;
    project_template_id?: string;
  }): Promise<Project> => {
    const { data } = await api.post('/projects', project);
    return apiResponseToProject(data);
  },

  /**
   * Update an existing project.
   * Can send individual fields or a full project payload.
   */
  update: async (projectId: string, updates: any): Promise<Project> => {
    const { data } = await api.patch(`/projects/${projectId}`, updates);
    // Any change that can move/relabel a project on the map invalidates the
    // cached locations so the next map open reflects it. Centralised here so
    // every caller (map placement, create, modify) is covered automatically.
    if (
      updates &&
      ("coordinates" in updates ||
        "infos" in updates ||
        "status" in updates)
    ) {
      locationsCache.invalidate();
    }
    return apiResponseToProject(data);
  },

  /**
   * Transfer a client contact's project access to a new email address.
   * Relinks the contact to the new email's account (existing or freshly
   * invited), revokes the old account's access, and notifies both sides
   * (the old account gets a transfer notice traced in "Emails envoyés").
   * Returns the updated project.
   */
  transferContactEmail: async (
    projectId: string,
    contactIndex: number,
    newEmail: string,
  ): Promise<Project> => {
    const { data } = await api.post(
      `/projects/${projectId}/contacts/transfer-email`,
      { contact_index: contactIndex, new_email: newEmail },
    );
    return apiResponseToProject(data);
  },

  /**
   * Save a full project (converts Timestamps and sends the whole structure).
   * Use this when the frontend has made local changes to the full project object.
   */
  save: async (project: Project): Promise<Project> => {
    const payload = projectToApiPayload(project);
    const { data } = await api.patch(`/projects/${project.id}`, {
      infos: payload.infos,
      themes: payload.themes,
      folders: payload.folders,
      photos: payload.photos,
      status: payload.status,
    // projectStore.updateProject renders its own message (and rolls the
    // optimistic row back), so skip the generic interceptor toast.
    }, { silenceGlobalError: true });
    return apiResponseToProject(data);
  },

  /**
   * Delete a project
   */
  delete: async (projectId: string): Promise<void> => {
    // Same as save: projectStore.removeProjects owns the failure message.
    await api.delete(`/projects/${projectId}`, { silenceGlobalError: true });
  },

  /**
   * Upload files (documents or photos) to a project's GCS storage.
   * Returns array of {id, url, name, content_type, size} for each uploaded file.
   */
  uploadFiles: async (projectId: string, files: File[]): Promise<UploadedFile[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const { data } = await api.post(`/projects/${projectId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  addPhotos: async (
    projectId: string,
    files: File[],
    controlpointId?: string,
    hidden?: boolean,
  ): Promise<Project> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (controlpointId) formData.append('controlpoint_id', controlpointId);
    if (hidden !== undefined) formData.append('hidden', String(hidden));
    const { data } = await api.post(`/projects/${projectId}/add-photos`, formData);
    return apiResponseToProject(data);
  },

  addDocuments: async (
    projectId: string,
    files: File[],
    folderId: string,
    subfolderId?: string,
    hidden?: boolean,
  ): Promise<Project> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('folder_id', folderId);
    if (subfolderId) formData.append('subfolder_id', subfolderId);
    if (hidden !== undefined) formData.append('hidden', String(hidden));
    const { data } = await api.post(`/projects/${projectId}/add-documents`, formData);
    return apiResponseToProject(data);
  },

  sendEmailWithHistory: async (
    projectId: string,
    emailData: {
      id?: string;
      subject: string;
      body: string;
      org_id: string;
      project_id: string;
      sender_user_id: string;
      email_template_id?: string;
      emails_to?: string[];
      emails_cc?: string[];
      emails_bcc?: string[];
      files: any[];
      photos_id?: string[];
      controlpoint_id?: string;
    }
  ): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/send-email`, emailData);
    return apiResponseToProject(data);
  },

  sendAccessEmail: async (
    projectId: string,
    userId: string,
    linkType: "auto" | "app" = "auto",
  ): Promise<{ message: string }> => {
    const { data } = await api.post(
      `/projects/${projectId}/send-access-email/${userId}`,
      undefined,
      { params: { link_type: linkType } },
    );
    return data;
  },

  markSeen: async (projectId: string, category?: "photos" | "documents" | "emails" | "signatures" | "billing_comments"): Promise<void> => {
    const query = category ? `?category=${category}` : "";
    await api.post(`/projects/${projectId}/mark-seen${query}`);
  },

  /** Append a comment to the project's billing-thread chat. Returns the
   * persisted comment so the caller can append it to local state. */
  addBillingComment: async (projectId: string, text: string) => {
    const { data } = await api.post(`/projects/${projectId}/billing-comments`, { text });
    return data as {
      id: string;
      text: string;
      author_id: string;
      author_name: string;
      author_photo_url?: string | null;
      created_at: string;
    };
  },

  /** Admin debug: dump raw Algolia records for the caller's org. */
  listAlgoliaRecords: async (orgId?: string): Promise<{ records: Record<string, any>[] }> => {
    const query = orgId ? `?org_id=${encodeURIComponent(orgId)}` : "";
    const { data } = await api.get(`/projects/algolia/records${query}`);
    return data;
  },

  /** Admin debug: trigger a full Algolia re-index for the caller's org. */
  syncAllAlgolia: async (orgId?: string): Promise<{ indexed: number; errors: number }> => {
    const query = orgId ? `?org_id=${encodeURIComponent(orgId)}` : "";
    const { data } = await api.post(`/projects/algolia/sync-all${query}`);
    return data;
  },

  updateControlpointState: async (
    projectId: string,
    controlpointId: string,
    newState: string
  ): Promise<Project> => {
    const { data } = await api.patch(`/projects/${projectId}/controlpoint`, {
      controlpoint_id: controlpointId,
      new_state: newState,
    });
    return apiResponseToProject(data);
  },

  // --- Billing actions ---

  triggerBillingInvoice: async (projectId: string, stepIndex: number): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/${stepIndex}/invoice`);
    return apiResponseToProject(data);
  },

  triggerDemarrageInvoice: async (projectId: string): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/demarrage/invoice`);
    return apiResponseToProject(data);
  },

  finalizeBillingInvoice: async (projectId: string, stepIndex: number): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/${stepIndex}/finalize`);
    return apiResponseToProject(data);
  },

  sendBillingInvoice: async (projectId: string, stepIndex: number): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/${stepIndex}/send`);
    return apiResponseToProject(data);
  },

  resendBillingInvoice: async (projectId: string, stepIndex: number): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/${stepIndex}/resend`);
    return apiResponseToProject(data);
  },

  retryBillingInvoice: async (projectId: string, stepIndex: number): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/${stepIndex}/retry`);
    return apiResponseToProject(data);
  },

  resetBillingStep: async (projectId: string, stepIndex: number, force = false): Promise<Project> => {
    const { data } = await api.post(
      `/projects/${projectId}/billing/${stepIndex}/reset`,
      undefined,
      { params: { force } },
    );
    return apiResponseToProject(data);
  },

  linkPennylaneInvoice: async (
    projectId: string,
    stepIndex: number,
    invoiceRef: string,
    force = false,
  ): Promise<Project> => {
    const { data } = await api.post(
      `/projects/${projectId}/billing/${stepIndex}/link-pennylane-invoice`,
      { invoice_ref: invoiceRef, force },
    );
    return apiResponseToProject(data);
  },

  // Set which controlpoints trigger a billing step (its linked_step_ids).
  // Self-service repair for a broken/missing controlpoint link.
  linkControlpoints: async (
    projectId: string,
    stepIndex: number,
    controlpointIds: string[],
  ): Promise<Project> => {
    const { data } = await api.post(
      `/projects/${projectId}/billing/${stepIndex}/link-controlpoints`,
      { controlpoint_ids: controlpointIds },
    );
    return apiResponseToProject(data);
  },

  syncBilling: async (projectId: string): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/sync`);
    return apiResponseToProject(data);
  },

  // Proxy-download the invoice PDF from the backend, which re-fetches a FRESH
  // URL from Pennylane on every call. Use this instead of the stored
  // `invoice_pdf_url` (which can be stale/manual and stop resolving).
  // Returns an object URL the caller must revoke when done.
  getBillingPdfUrl: async (projectId: string, stepIndex: number): Promise<string> => {
    const { data } = await api.get(
      `/projects/${projectId}/billing/${stepIndex}/pdf`,
      { responseType: "blob" },
    );
    return URL.createObjectURL(data as Blob);
  },

  archiveBilling: async (projectId: string, archived: boolean): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/archive`, { archived });
    return apiResponseToProject(data);
  },

  repairBillingStep: async (projectId: string, stepIndex: number): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/${stepIndex}/repair`);
    return apiResponseToProject(data);
  },

  repairProjectBilling: async (projectId: string): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/billing/repair`);
    return apiResponseToProject(data);
  },

  // Admin-only: wipe a project's entire billing (used when a contract is
  // renegotiated). Works even with already-processed steps. Pennylane invoices
  // are left intact — the billing is only detached on our side.
  deleteBilling: async (projectId: string): Promise<Project> => {
    const { data } = await api.delete(`/projects/${projectId}/billing`);
    return apiResponseToProject(data);
  },

  syncAllBilling: async (billingTemplateId?: string): Promise<Project[]> => {
    const { data } = await api.post('/projects/actions/billing-sync', null, {
      params: billingTemplateId ? { billing_template_id: billingTemplateId } : {},
    });
    return (data as any[]).map(apiResponseToProject);
  },
};

export interface UploadedFile {
  id: string;
  url: string;
  name: string;
  content_type: string;
  size: number;
}
